// Module 03 — DCSystem. Assembles the reduced B' (grounded Laplacian, slack row/col
// dropped) per island as CSR, solves B'·θ = P with warm-started CG (dense Cholesky for
// tiny islands), pins slack θ=0, zeroes blacked-out islands. refactor() rebuilds the
// CSR + island map on a topology change; steady ticks reuse it and warm-start from θ.
import type { GridNode, GridEdge, EdgeId, NodeId } from "../domain/index";
import { type Csr, cg, choleskySolve } from "./cg";
import { findIslands } from "./islands";

// engine.v3 P6 — a compact, READ-ONLY view of one per-island reduced-B′ factorization, handed to
// sensitivities.ts (LODF/PTDF/Woodbury) so the SAME B′ dc.ts already assembled is the single source of
// truth for the sensitivity math (no second Laplacian in the engine). Reflects the LAST refactor().
export interface DcSensitivityCtx {
  readonly keep: readonly number[]; // global node idx in CSR row order
  readonly csr: Csr; // the island's reduced (slack-dropped) B′
  readonly slackGlobal: number | null; // dropped reference (global idx), null if the island has none
  readonly localOf: ReadonlyMap<number, number>; // global→local (absent ⇒ this island's slack or another island)
}

export interface DCSystem {
  readonly nNodes: number;
  setInjections(p: Float64Array): void; // per-unit; slack entry ignored
  solveAngles(): Float64Array; // θ (rad), length nNodes
  refactor(openEdges: ReadonlySet<EdgeId>): void;
  // P6 (additive, powerflow.v1 addendum): one ctx per non-blacked-out island, reusing the CURRENT plans[].
  sensitivityCtx(): DcSensitivityCtx[];
}

const CG_TOL = 1e-11;
const CG_MAXITER = 10000;
const DENSE_MAX = 2; // islands with ≤2 unknowns use the direct Cholesky fallback
const RAD2DEG = 180 / Math.PI;

// One assembled, non-blacked-out island: reduced B' (CSR) over its non-slack buses.
interface IslandPlan {
  keep: number[]; // global node indices, in CSR row order
  csr: Csr;
  slackIdx: number | null;
  all: number[]; // every global node index in this island
  blackout: boolean;
  localOf: Map<number, number>; // P6: global→local (persisted for sensitivityCtx; was transient in refactor)
}

// Per-branch |θu − θv| in degrees (DC-validity diagnostic; ±30° is the flag threshold).
export function branchAngleDiffsDeg(
  edges: readonly GridEdge[], theta: Float64Array, idOf: (id: NodeId) => number,
): Float64Array {
  const out = new Float64Array(edges.length);
  for (let e = 0; e < edges.length; e++) {
    const iu = idOf(edges[e]!.u);
    const iv = idOf(edges[e]!.v);
    out[e] = iu >= 0 && iv >= 0 ? Math.abs(theta[iu]! - theta[iv]!) * RAD2DEG : 0;
  }
  return out;
}

class DcSystemImpl implements DCSystem {
  readonly nNodes: number;
  private readonly index: Map<NodeId, number>;
  private readonly p: Float64Array;
  private readonly theta: Float64Array; // persists across ticks → CG warm start
  private plans: IslandPlan[] = [];
  // Reused solve scratch (sized to nNodes ≥ any island's keep-count); sliced to length k
  // via subarray each plan — avoids a per-island, per-tick O(k) Float64Array alloc.
  // cg() copies x0 internally and never mutates b/x0, so reuse is bit-identical.
  private readonly rhsScratch: Float64Array;
  private readonly x0Scratch: Float64Array;

  constructor(
    private readonly nodes: readonly GridNode[],
    private readonly edges: readonly GridEdge[],
    private readonly slack: NodeId,
  ) {
    this.nNodes = nodes.length;
    this.index = new Map();
    for (let i = 0; i < nodes.length; i++) this.index.set(nodes[i]!.id, i);
    this.p = new Float64Array(this.nNodes);
    this.theta = new Float64Array(this.nNodes);
    this.rhsScratch = new Float64Array(this.nNodes);
    this.x0Scratch = new Float64Array(this.nNodes);
    this.refactor(new Set());
  }

  setInjections(p: Float64Array): void {
    this.p.set(p.subarray(0, this.nNodes));
  }

  refactor(openEdges: ReadonlySet<EdgeId>): void {
    // ONE shared live-edge predicate drives BOTH islanding and B′ assembly (C73): a bus reachable
    // only through a zero/∞-impedance edge must not become a ghost-connected singular B′ row — it
    // is its own (blackout) island in both traversals. Built once per topology change (not per tick).
    const isLive = (e: GridEdge): boolean =>
      !openEdges.has(e.id) && e.xPu !== 0 && Number.isFinite(e.xPu);
    const islands = findIslands(this.nodes, this.edges, openEdges, this.slack, isLive);
    const islandOf = new Int32Array(this.nNodes).fill(-1);
    const all: number[][] = islands.map(() => []);
    islands.forEach((isl, ci) => {
      for (const id of isl.nodes) {
        const g = this.index.get(id)!;
        islandOf[g] = ci;
        all[ci]!.push(g);
      }
    });

    // per-island local indexing over the non-slack ("keep") buses
    const keep: number[][] = islands.map(() => []);
    const localOf: Array<Map<number, number>> = islands.map(() => new Map());
    const slackIdx: Array<number | null> = islands.map((isl) =>
      isl.slack !== null ? this.index.get(isl.slack)! : null,
    );
    islands.forEach((_isl, ci) => {
      for (const g of all[ci]!) {
        if (g === slackIdx[ci]) continue;
        localOf[ci]!.set(g, keep[ci]!.length);
        keep[ci]!.push(g);
      }
    });

    // accumulate reduced B' = grounded Laplacian (rows[ci][localRow] : localCol → value)
    const rows: Array<Array<Map<number, number>>> = islands.map((_isl, ci) =>
      keep[ci]!.map(() => new Map<number, number>()),
    );
    const addAt = (ci: number, lr: number, lc: number, dv: number): void => {
      const m = rows[ci]![lr]!;
      m.set(lc, (m.get(lc) ?? 0) + dv);
    };
    for (const e of this.edges) {
      if (!isLive(e)) continue; // SAME predicate as findIslands above — they cannot disagree (C73).
      const x = e.xPu; // negative x (series capacitor, b=1/x<0) is physical and kept.
      const ga = this.index.get(e.u);
      const gb = this.index.get(e.v);
      if (ga === undefined || gb === undefined || ga === gb) continue;
      const ci = islandOf[ga]!;
      if (ci < 0 || ci !== islandOf[gb]! || islands[ci]!.blackout) continue;
      // engine.v3 P3: the MATPOWER pure-DC off-nominal transformer coefficient — makeBdc applies
      // b = (1/x)/tap SYMMETRICALLY (same scaled b on diagonal and off-diagonal; NOT the AC b/t²
      // asymmetry). dc-solve.ts scales the FLOW readout by the same tap ((θu−θv)/(x·tap)) so the
      // reported flows satisfy KCL — the two MUST move together (a tapped B′ with an un-tapped readout
      // breaks conservation, which the P2 harness would catch). Guarded: an absent/non-positive tapRatio
      // is EXACTLY the historical 1/x path ⇒ golden byte-identical by construction (no golden edge carries
      // tapRatio; Convention X emits none on live data either — %Z-derived xPu carries the physics).
      const t = e.tapRatio;
      const b = t !== undefined && t > 0 ? 1 / x / t : 1 / x;
      const la = localOf[ci]!.get(ga);
      const lb = localOf[ci]!.get(gb);
      if (la !== undefined) addAt(ci, la, la, b); // diagonal (slack col dropped)
      if (lb !== undefined) addAt(ci, lb, lb, b);
      if (la !== undefined && lb !== undefined) {
        addAt(ci, la, lb, -b);
        addAt(ci, lb, la, -b);
      }
    }

    this.plans = islands.map((isl, ci) => ({
      keep: keep[ci]!,
      csr: buildCsr(keep[ci]!.length, rows[ci]!),
      slackIdx: slackIdx[ci] ?? null,
      all: all[ci]!,
      blackout: isl.blackout,
      localOf: localOf[ci]!, // P6: persist the per-island global→local map for sensitivityCtx()
    }));
  }

  // P6 (additive): expose the per-island factorization sensitivities.ts needs — read-only views of the
  // CURRENT plans[] (skips blacked-out islands). Reflects the last refactor(); never mutates plans/θ/p.
  sensitivityCtx(): DcSensitivityCtx[] {
    const out: DcSensitivityCtx[] = [];
    for (const plan of this.plans) {
      if (plan.blackout || plan.keep.length === 0) continue;
      out.push({ keep: plan.keep, csr: plan.csr, slackGlobal: plan.slackIdx, localOf: plan.localOf });
    }
    return out;
  }

  solveAngles(): Float64Array {
    for (const plan of this.plans) {
      if (plan.blackout) {
        for (const g of plan.all) this.theta[g] = 0;
        continue;
      }
      if (plan.slackIdx !== null) this.theta[plan.slackIdx] = 0;
      const k = plan.keep.length;
      if (k === 0) continue;
      const rhs = this.rhsScratch.subarray(0, k);
      for (let i = 0; i < k; i++) rhs[i] = this.p[plan.keep[i]!]!;
      let sol: Float64Array;
      let converged = true;
      if (k <= DENSE_MAX) {
        sol = choleskySolve(csrToDense(plan.csr), rhs);
      } else {
        const x0 = this.x0Scratch.subarray(0, k);
        for (let i = 0; i < k; i++) x0[i] = this.theta[plan.keep[i]!]!;
        const res = cg(plan.csr, rhs, x0, CG_TOL, CG_MAXITER);
        sol = res.x;
        converged = res.converged;
      }
      // Never emit a poisoned θ (C70/C71): a non-converged CG or a non-finite solve (a
      // singular/indefinite island — choleskySolve yields NaN from sqrt(≤0)) is a RUNTIME blackout
      // for this tick — zero the whole island's θ rather than propagate NaN/garbage into flows +
      // loss recovery. Latent on shipped data (0 zero-/negative-xPu edges) ⇒ golden byte-identical.
      if (converged) {
        for (let i = 0; i < k; i++) { if (!Number.isFinite(sol[i]!)) { converged = false; break; } }
      }
      if (!converged) {
        plan.blackout = true; // P17 H4: flag the island so sensitivityCtx skips it (was only zeroing θ)
        for (const g of plan.all) this.theta[g] = 0;
        continue;
      }
      for (let i = 0; i < k; i++) this.theta[plan.keep[i]!] = sol[i]!;
    }
    return Float64Array.from(this.theta);
  }
}

function buildCsr(keepLen: number, rows: Array<Map<number, number>>): Csr {
  const rowPtr = new Int32Array(keepLen + 1);
  const cols: number[] = [];
  const vals: number[] = [];
  for (let i = 0; i < keepLen; i++) {
    const entries = [...rows[i]!.entries()].sort((a, b) => a[0] - b[0]);
    for (const [c, v] of entries) {
      cols.push(c);
      vals.push(v);
    }
    rowPtr[i + 1] = cols.length;
  }
  return { n: keepLen, rowPtr, colIdx: Int32Array.from(cols), val: Float64Array.from(vals) };
}

function csrToDense(A: Csr): number[][] {
  const d = Array.from({ length: A.n }, () => new Array<number>(A.n).fill(0));
  for (let i = 0; i < A.n; i++) {
    for (let k = A.rowPtr[i]!; k < A.rowPtr[i + 1]!; k++) d[i]![A.colIdx[k]!] = A.val[k]!;
  }
  return d;
}

export function buildDC(
  nodes: readonly GridNode[], edges: readonly GridEdge[], slack: NodeId,
): DCSystem {
  return new DcSystemImpl(nodes, edges, slack);
}
